import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  CalendarIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { guardianApi } from '../../services/api/guardian';
import { useGuardianAuthStore } from '../../stores/guardianAuthStore';
import { Card, H1, H2, H3, Text, Badge, Button, Spinner, Alert } from '../../components/ui';
import { formatDate } from '../../utils/date';

export const GuardianDashboard: React.FC = () => {
  const { guardian, setChildren } = useGuardianAuthStore();

  // Fetch children
  const { data: children, isLoading: childrenLoading, error: childrenError } = useQuery({
    queryKey: ['guardian-children'],
    queryFn: guardianApi.portal.getChildren,
  });

  // Store children in auth store for easy access
  useEffect(() => {
    if (children) {
      setChildren(children);
    }
  }, [children, setChildren]);

  // Fetch recent observations for all children
  const { data: recentObservations } = useQuery({
    queryKey: ['guardian-recent-observations', children],
    queryFn: async () => {
      if (!children || children.length === 0) return [];
      
      const allObservations = await Promise.all(
        children.map(child =>
          guardianApi.portal.getChildObservations(child.id, { limit: 3 })
            .then(obs => obs.map(o => ({ ...o, childName: `${child.first_name} ${child.last_name}` })))
        )
      );
      
      return allObservations
        .flat()
        .sort((a, b) => new Date(b.observation_date).getTime() - new Date(a.observation_date).getTime())
        .slice(0, 5);
    },
    enabled: !!children && children.length > 0,
  });

  // Fetch recent assessments for all children
  const { data: recentAssessments } = useQuery({
    queryKey: ['guardian-recent-assessments', children],
    queryFn: async () => {
      if (!children || children.length === 0) return [];
      
      const allAssessments = await Promise.all(
        children.map(child =>
          guardianApi.portal.getChildAssessments(child.id, { limit: 3 })
            .then(assess => assess.map(a => ({ ...a, childName: `${child.first_name} ${child.last_name}` })))
        )
      );
      
      return allAssessments
        .flat()
        .sort((a, b) => new Date(b.assessment_date).getTime() - new Date(a.assessment_date).getTime())
        .slice(0, 5);
    },
    enabled: !!children && children.length > 0,
  });

  if (childrenLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <Alert variant="error">
        Failed to load dashboard data. Please try again later.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <H1>Parent Portal Dashboard</H1>
        <Text className="mt-2 text-gray-600 dark:text-gray-400">
          Welcome back, {guardian?.first_name}! Here's an overview of your children's progress.
        </Text>
      </div>

      {/* Children Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {children?.map((child) => (
          <Link key={child.id} to={`/guardian/children/${child.id}`}>
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-start justify-between">
                <div>
                  <H3>{child.first_name} {child.last_name}</H3>
                  <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {child.grade?.name} - {child.classroom?.name}
                  </Text>
                  <Text className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Age: {child.age != null
                      ? child.age
                      : child.date_of_birth
                        ? Math.floor((Date.now() - new Date(child.date_of_birth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
                        : 'Unknown'} years
                  </Text>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                    <UserGroupIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-primary-600 dark:text-primary-400">
                View Details
                <ArrowRightIcon className="ml-1 w-4 h-4" />
              </div>
            </Card>
          </Link>
        ))}

        {(!children || children.length === 0) && (
          <Card className="p-6 col-span-full">
            <div className="text-center">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <H3 className="mt-2 text-gray-900 dark:text-gray-100">No Children Found</H3>
              <Text className="mt-1 text-gray-500 dark:text-gray-400">
                You don't have any children linked to your account yet.
              </Text>
            </div>
          </Card>
        )}
      </div>

      {/* Recent Activity */}
      {children && children.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Observations */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <H2 className="flex items-center">
                <ClipboardDocumentListIcon className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
                Recent Observations
              </H2>
              <Link to="/guardian/observations">
                <Button variant="ghost" size="sm">
                  View All
                </Button>
              </Link>
            </div>
            
            <div className="space-y-3">
              {recentObservations?.map((observation) => (
                <div key={observation.id} className="border-l-2 border-primary-200 dark:border-primary-700 pl-4 py-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <Text className="font-medium text-sm">
                        {observation.childName}
                      </Text>
                      <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                        {observation.narrative}
                      </Text>
                      <div className="flex items-center mt-2 space-x-3">
                        <Badge variant="secondary" size="sm">
                          {observation.category}
                        </Badge>
                        <Text className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(observation.observation_date)}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {(!recentObservations || recentObservations.length === 0) && (
                <Text className="text-gray-500 dark:text-gray-400 text-sm">
                  No recent observations available
                </Text>
              )}
            </div>
          </Card>

          {/* Recent Assessments */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <H2 className="flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
                Recent Assessments
              </H2>
              <Link to="/guardian/assessments">
                <Button variant="ghost" size="sm">
                  View All
                </Button>
              </Link>
            </div>
            
            <div className="space-y-3">
              {recentAssessments?.map((assessment) => (
                <div key={assessment.id} className="border-l-2 border-green-200 dark:border-green-700 pl-4 py-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <Text className="font-medium text-sm">
                        {assessment.childName}
                      </Text>
                      <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {assessment.learning_area_name || assessment.learning_area?.name}: {assessment.learning_outcome?.description}
                      </Text>
                      <div className="flex items-center mt-2 space-x-3">
                        {assessment.score != null && (
                          <Badge variant="success" size="sm">
                            Score: {assessment.score}/{assessment.assessment_scale?.max_value}
                          </Badge>
                        )}
                        <Text className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(assessment.assessment_date)}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {(!recentAssessments || recentAssessments.length === 0) && (
                <Text className="text-gray-500 dark:text-gray-400 text-sm">
                  No recent assessments available
                </Text>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <Card className="p-6">
        <H2 className="mb-4">Quick Actions</H2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/guardian/children">
            <Button variant="outline" className="w-full justify-start">
              <UserGroupIcon className="w-5 h-5 mr-2" />
              View Children
            </Button>
          </Link>
          <Link to="/guardian/observations">
            <Button variant="outline" className="w-full justify-start">
              <ClipboardDocumentListIcon className="w-5 h-5 mr-2" />
              All Observations
            </Button>
          </Link>
          <Link to="/guardian/assessments">
            <Button variant="outline" className="w-full justify-start">
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              All Assessments
            </Button>
          </Link>
          <Link to="/guardian/profile">
            <Button variant="outline" className="w-full justify-start">
              <CalendarIcon className="w-5 h-5 mr-2" />
              My Profile
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
};